// src/components/ChargerStatusDetailModal.tsx
import React from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Dimensions,
  Platform,
  ScrollView,
} from 'react-native';
import { BlurView } from 'expo-blur';
import {
  ChargerDisplayStatus,
  ChargerErrorSeverity,
  getStatusColor,
} from '../models/charger-model';
import { MaterialIcons } from '@expo/vector-icons';

const deviceWidth = Dimensions.get('window').width;
const horizontalPadding = 20;
const modalWidth = deviceWidth - horizontalPadding * 2;

interface ChargerStatusDetailModalProps {
  visible: boolean;
  onClose: () => void;
  chargerName: string;
  status: ChargerDisplayStatus;
  errorCode?: string | null;
  errorDescription?: string | null;
  errorUserMessage?: string | null;
  errorSeverity?: ChargerErrorSeverity | string | null;
  errorRecommendedAction?: string | null;
  vendorId?: string | null;
  vendorErrorCode?: string | null;
  /** When provided, renders a "Start Troubleshooting" CTA that opens the
   *  guided host troubleshooting flow. The caller decides when to surface it
   *  (e.g. demo mode + fault status). */
  onStartTroubleshooting?: () => void;
}

const getSeverityIcon = (severity: string | null | undefined): keyof typeof MaterialIcons.glyphMap => {
  switch (severity) {
    case 'critical':
      return 'error';
    case 'error':
      return 'warning';
    case 'warning':
      return 'warning';
    case 'info':
    default:
      return 'info';
  }
};

const getSeverityLabel = (severity: string | null | undefined): string => {
  switch (severity) {
    case 'critical':
      return 'Critical Issue';
    case 'error':
      return 'Error';
    case 'warning':
      return 'Warning';
    case 'info':
    default:
      return 'Information';
  }
};

const ChargerStatusDetailModal: React.FC<ChargerStatusDetailModalProps> = ({
  visible,
  onClose,
  chargerName,
  status,
  errorCode,
  errorDescription,
  errorUserMessage,
  errorSeverity,
  errorRecommendedAction,
  vendorId,
  vendorErrorCode,
  onStartTroubleshooting,
}) => {
  const statusColor = getStatusColor(status, errorSeverity);
  const severityIcon = getSeverityIcon(errorSeverity);
  const severityLabel = getSeverityLabel(errorSeverity);

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.absoluteContainer}>
        <BlurView intensity={50} tint="dark" style={styles.modalBackground}>
          {/* Backdrop - closes modal when tapped */}
          <Pressable style={styles.backdrop} onPress={onClose} />
          
          {/* Modal Content - doesn't close when tapped */}
          <View style={styles.modalContainer}>
            {/* Header with Status Icon */}
            <View style={styles.headerContainer}>
              <View style={[styles.statusIconContainer, { backgroundColor: statusColor }]}>
                <MaterialIcons name={severityIcon} size={28} color="#FFFFFF" />
              </View>
              <Text style={styles.headerText}>{chargerName}</Text>
              <View style={[styles.severityBadge, { backgroundColor: statusColor }]}>
                <Text style={styles.severityText}>{severityLabel}</Text>
              </View>
            </View>

            <View style={styles.separator} />

            {/* Status Message - Scrollable Content */}
            <ScrollView 
              style={styles.scrollContent} 
              contentContainerStyle={styles.scrollContentContainer}
              showsVerticalScrollIndicator={true}
              bounces={true}
              alwaysBounceVertical={true}
              overScrollMode="always"
            >
              {/* User Message (primary status) */}
              {errorUserMessage && (
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>Status</Text>
                  <Text style={styles.userMessage}>{errorUserMessage}</Text>
                </View>
              )}

              {/* Description */}
              {errorDescription && (
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>What This Means</Text>
                  <Text style={styles.descriptionText}>{errorDescription}</Text>
                </View>
              )}

              {/* Recommended Action */}
              {errorRecommendedAction && (
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>What You Can Do</Text>
                  <View style={styles.actionContainer}>
                    <MaterialIcons name="lightbulb" size={20} color="#51B868" style={styles.actionIcon} />
                    <Text style={styles.actionText}>{errorRecommendedAction}</Text>
                  </View>
                </View>
              )}

              {/* Technical Details (show error codes) */}
              {(errorCode || vendorId || vendorErrorCode) && (
                <View style={styles.technicalSection}>
                  <Text style={styles.technicalTitle}>Technical Details</Text>
                  {errorCode && (
                    <Text style={styles.technicalText}>Error Code: {errorCode}</Text>
                  )}
                  {vendorId && (
                    <Text style={styles.technicalText}>Vendor: {vendorId}</Text>
                  )}
                  {vendorErrorCode && (
                    <Text style={styles.technicalText}>Vendor Code: {vendorErrorCode}</Text>
                  )}
                </View>
              )}
            </ScrollView>

            {onStartTroubleshooting && (
              <TouchableOpacity
                style={styles.troubleshootButton}
                onPress={onStartTroubleshooting}
              >
                <MaterialIcons name="build" size={18} color="#FFFFFF" />
                <Text style={styles.troubleshootButtonText}>
                  Start Troubleshooting
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Text style={styles.closeButtonText}>Got It</Text>
            </TouchableOpacity>
          </View>
        </BlurView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  absoluteContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalBackground: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modalContainer: {
    backgroundColor: '#292D33',
    width: modalWidth * 0.95,
    maxWidth: 400,
    maxHeight: '80%',
    padding: 24,
    borderRadius: 16,
    alignItems: 'center',
    flexShrink: 1,
  },
  headerContainer: {
    alignItems: 'center',
    marginBottom: 8,
  },
  statusIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerText: {
    fontFamily: 'PlusJakartaSans-Bold',
    fontWeight: '700',
    fontSize: 20,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 8,
  },
  severityBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  severityText: {
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontWeight: '600',
    fontSize: 12,
    color: '#FFFFFF',
  },
  separator: Platform.select({
    web: {
      alignSelf: 'stretch',
      borderBottomWidth: 1,
      borderStyle: 'dashed',
      borderColor: '#5F6164',
      marginVertical: 16,
    },
    default: {
      alignSelf: 'stretch',
      borderBottomWidth: 1,
      borderColor: '#5F6164',
      marginVertical: 16,
    },
  }),
  scrollContent: {
    alignSelf: 'stretch',
    flexGrow: 1,
    flexShrink: 1,
  },
  scrollContentContainer: {
    paddingBottom: 8,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontWeight: '600',
    fontSize: 14,
    color: '#51B868',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  userMessage: {
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontWeight: '600',
    fontSize: 18,
    color: '#FFFFFF',
    lineHeight: 24,
  },
  descriptionText: {
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '400',
    fontSize: 15,
    color: '#B0B1B3',
    lineHeight: 22,
  },
  actionContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#1E2227',
    padding: 12,
    borderRadius: 8,
  },
  actionIcon: {
    marginRight: 10,
    marginTop: 2,
  },
  actionText: {
    flex: 1,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '400',
    fontSize: 15,
    color: '#FFFFFF',
    lineHeight: 22,
  },
  technicalSection: {
    marginTop: 8,
    padding: 12,
    backgroundColor: '#1E2227',
    borderRadius: 8,
  },
  technicalTitle: {
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontWeight: '600',
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  technicalText: {
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '400',
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 4,
  },
  troubleshootButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#F59E0B',
    borderRadius: 45,
    width: '100%',
    height: 50,
    marginTop: 16,
  },
  troubleshootButtonText: {
    color: '#FFFFFF',
    fontFamily: 'PlusJakartaSans-Bold',
    fontWeight: '700',
    fontSize: 15,
  },
  closeButton: {
    backgroundColor: '#51B868',
    borderRadius: 45,
    width: '100%',
    height: 50,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 12,
  },
  closeButtonText: {
    color: '#FFFFFF',
    fontFamily: 'PlusJakartaSans-Bold',
    fontWeight: '700',
    fontSize: 16,
  },
});

export default ChargerStatusDetailModal;
